import { apiClient } from "@api/client";
import { request, requestVoid } from "@api/request";
import type { components } from "@api/schema";

type StaffPhotoWire = components["schemas"]["StaffPhotoOut"];

export type NormalizedBox = components["schemas"]["NormalizedBox"];

/** Mirrors brian's `StaffPhotoPose`; change both together. The room poses are an admin's capture and are never uploaded. */
export type StaffPhotoPose = components["schemas"]["StaffPhotoPose"];

/** Mirrors brian's `StaffPhotoPoseGroup`; change both together. `above` is shot with the camera held over the head. */
type StaffPhotoPoseGroup = components["schemas"]["StaffPhotoPoseGroup"];

/** Mirrors brian's `StaffPhotoPoseOut`; change both together. The wording is brian's, never the app's. */
export interface StaffPhotoPoseGuide {
  pose: StaffPhotoPose;
  label: string;
  instruction: string;
  group: StaffPhotoPoseGroup;
}

/** Mirrors brian's `StaffPhotoOut`; change both together. */
export interface StaffPhoto {
  id: string;
  userId: string;
  /** The room whose camera the photo was cropped from; null for a guided self-photo and for an earlier capture that did not record it. */
  roomId: string | null;
  status: components["schemas"]["StaffPhotoStatus"];
  pose: StaffPhotoPose;
  /** brian `StaffPhotoSource`: `camera` is an admin's crop from a room still, `upload` a guided self-photo. */
  source: string;
  createdAt: string;
  confirmedAt: string | null;
  /** Short-lived; only the photo's subject is ever sent one. */
  imageUrl: string | null;
}

/** Mirrors brian's `MyStaffPhotosOut`. The consent text is brian's, so the version confirmed is the text shown. */
interface MyStaffPhotos {
  consentVersion: string;
  consentText: string;
  /** Whether the person holds current one-time consent to room-camera photos. */
  consented: boolean;
  /** The guided upload poses, in capture order. */
  poses: StaffPhotoPoseGuide[];
  pending: StaffPhoto[];
  /** At most one guided photo per pose; up to three room-camera photos per room. */
  confirmed: StaffPhoto[];
}

/** Mirrors brian's `StaffPhotoCoverageOut`: counts only, never images. */
export interface StaffPhotoCoverage {
  /** The rooms that have sent a still: the cameras an admin can capture from. */
  roomsWithStills: string[];
  staff: StaffCoverage[];
}

export interface StaffCoverage {
  userId: string;
  displayName: string;
  consented: boolean;
  guidedPhotos: number;
  roomPhotos: { roomId: string | null; confirmed: number; pending: number }[];
}

const toPhoto = (p: StaffPhotoWire): StaffPhoto => ({
  id: p.uuid,
  userId: p.user_uuid,
  roomId: p.room_uuid,
  status: p.status,
  pose: p.pose,
  source: p.source,
  createdAt: p.created_at,
  confirmedAt: p.confirmed_at,
  imageUrl: p.image_url ?? null,
});

export const staffPhotoClient = {
  getMine: (): Promise<MyStaffPhotos> =>
    request(() => apiClient.GET("/staff-photos/me", {})).then((m) => ({
      consentVersion: m.consent_version,
      consentText: m.consent_text,
      consented: m.consented,
      poses: m.poses,
      pending: m.pending.map(toPhoto),
      confirmed: m.confirmed.map(toPhoto),
    })),

  /**
   * Replaces the confirmed photo of that pose only. `guided`: a webcam frame
   * with the face in the capture oval, which brian crops to a square round it.
   */
  upload: (
    file: Blob,
    consentVersion: string,
    pose: StaffPhotoPose,
    guided: boolean,
  ): Promise<StaffPhoto> =>
    request(() =>
      apiClient.POST("/staff-photos/me", {
        // openapi-typescript types binary parts as `string`; the serializer is what crosses the wire.
        body: {
          file: file as unknown as string,
          consent_version: consentVersion,
          pose,
          guided,
        },
        bodySerializer: () => {
          const form = new FormData();
          form.append("file", file, "photo.jpg");
          form.append("consent_version", consentVersion);
          form.append("pose", pose);
          form.append("guided", String(guided));
          return form;
        },
      }),
    ).then(toPhoto),

  confirm: (photoId: string, consentVersion: string): Promise<StaffPhoto> =>
    request(() =>
      apiClient.POST("/staff-photos/me/{photo_uuid}/confirm", {
        params: { path: { photo_uuid: photoId } },
        body: { consent_version: consentVersion },
      }),
    ).then(toPhoto),

  remove: (photoId: string): Promise<void> =>
    requestVoid(() =>
      apiClient.DELETE("/staff-photos/{photo_uuid}", {
        params: { path: { photo_uuid: photoId } },
      }),
    ),

  consent: (consentVersion: string): Promise<void> =>
    requestVoid(() =>
      apiClient.POST("/staff-photos/me/consent", {
        body: { consent_version: consentVersion },
      }),
    ),

  /** Leadership only. */
  coverage: (): Promise<StaffPhotoCoverage> =>
    request(() => apiClient.GET("/staff-photos/coverage", {})).then((c) => ({
      roomsWithStills: c.rooms_with_stills,
      staff: c.staff.map((r) => ({
        userId: r.user_uuid,
        displayName: r.display_name,
        consented: r.consented,
        guidedPhotos: r.guided_photos,
        roomPhotos: r.room_photos.map((p) => ({
          roomId: p.room_uuid,
          confirmed: p.confirmed,
          pending: p.pending,
        })),
      })),
    })),

  /**
   * Leadership only. Each shot's box is 0–1 of its still's width and height;
   * brian crops it from its own copy of that still, so no image leaves the
   * browser. The set replaces the person's earlier photos from the room.
   */
  capture: (body: {
    userId: string;
    roomId: string;
    shots: { stillReference: string; box: NormalizedBox }[];
  }): Promise<StaffPhoto[]> =>
    request(() =>
      apiClient.POST("/staff-photos/captures", {
        body: {
          user_uuid: body.userId,
          room_uuid: body.roomId,
          shots: body.shots.map((shot) => ({
            still_reference: shot.stillReference,
            box: shot.box,
          })),
          consent: true,
        },
      }),
    ).then((photos) => photos.map(toPhoto)),
};
